import React from 'react';
import { Box, Link } from '@mui/material';
import { parseAnswer } from './answerMarkdown';

const cellSx = {
  border: '1px solid',
  borderColor: 'divider',
  px: 1,
  py: 0.5,
  textAlign: 'left',
  verticalAlign: 'top',
};

const renderInlines = (inlines, keyPrefix) =>
  (inlines || []).map((token, index) => {
    const key = `${keyPrefix}-${index}`;
    if (token.type === 'bold') {
      return (
        <Box key={key} component='strong' sx={{ fontWeight: 700 }}>
          {token.text}
        </Box>
      );
    }
    if (token.type === 'link') {
      return (
        <Link key={key} href={token.url} target='_blank' rel='noopener noreferrer' underline='hover'>
          {token.text}
        </Link>
      );
    }
    const parts = String(token.text || '').split('\n');
    return (
      <React.Fragment key={key}>
        {parts.map((part, partIndex) => (
          <React.Fragment key={`${key}-${partIndex}`}>
            {partIndex > 0 ? <br /> : null}
            {part}
          </React.Fragment>
        ))}
      </React.Fragment>
    );
  });

const AnswerBody = ({ text }) => {
  const blocks = parseAnswer(text);
  if (!blocks.length) return null;

  return (
    <Box
      sx={{
        overflowWrap: 'anywhere',
        '& p': { m: 0, mb: 1, lineHeight: 1.7, '&:last-child': { mb: 0 } },
        '& ul, & ol': { m: 0, pl: 2.5, mb: 1 },
        '& li': { mb: 0.5, lineHeight: 1.7 },
        '& a': { overflowWrap: 'anywhere' },
      }}
    >
      {blocks.map((block, index) => {
        const key = `block-${index}`;
        if (block.type === 'list') {
          const ListTag = block.ordered ? 'ol' : 'ul';
          return (
            <Box key={key} component={ListTag}>
              {block.items.map((item, itemIndex) => (
                <Box key={`${key}-${itemIndex}`} component='li'>
                  {renderInlines(item, `${key}-${itemIndex}`)}
                </Box>
              ))}
            </Box>
          );
        }
        if (block.type === 'table') {
          return (
            <Box key={key} sx={{ overflowX: 'auto', mb: 1 }}>
              <Box component='table' sx={{ borderCollapse: 'collapse', minWidth: '100%' }}>
                <Box component='thead'>
                  <Box component='tr'>
                    {block.headers.map((cell, cellIndex) => (
                      <Box key={`${key}-h-${cellIndex}`} component='th' sx={{ ...cellSx, fontWeight: 700 }}>
                        {renderInlines(cell, `${key}-h-${cellIndex}`)}
                      </Box>
                    ))}
                  </Box>
                </Box>
                <Box component='tbody'>
                  {block.rows.map((row, rowIndex) => (
                    <Box key={`${key}-r-${rowIndex}`} component='tr'>
                      {row.map((cell, cellIndex) => (
                        <Box key={`${key}-c-${rowIndex}-${cellIndex}`} component='td' sx={cellSx}>
                          {renderInlines(cell, `${key}-c-${rowIndex}-${cellIndex}`)}
                        </Box>
                      ))}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          );
        }
        return (
          <Box key={key} component='p'>
            {renderInlines(block.inlines, key)}
          </Box>
        );
      })}
    </Box>
  );
};

export default AnswerBody;
